/*
 * Lien entre le CRM et la table participants : la même table que le formulaire participant de la page admin.
 * Une fois le membre actif, ses fiches participants font foi : le CRM les relit à l'ouverture
 * et y écrit les champs modifiés, avec les mêmes contrôles que le formulaire admin.
 */
import { supabase } from '../../lib/supabase';
import { noRowError } from './session';
import type { CrmContact, CrmContactDraft, EntityType, Meter, ParticipantRow, Role, VatRegime } from './types';
import { PARTICIPANT_COLUMNS, VAT_RATES, formatBce } from './types';

/** c€/kWh (CRM) → €/MWh (participants) */
export const toMwh = (v: number | null | undefined): number | null =>
  v === null || v === undefined || String(v) === '' ? null : Math.round(Number(v) * 10 * 10000) / 10000;
/** €/MWh (participants) → c€/kWh (CRM) */
export const fromMwh = (v: number | null | undefined): number | null =>
  v === null || v === undefined ? null : Math.round((Number(v) / 10) * 10000) / 10000;

const escapeLike = (v: string) => v.trim().replace(/[%_\\]/g, m => `\\${m}`);
const lower = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

/** Taux de TVA proposé au passage en « Membre actif » (modifiable). */
export function defaultVatRate(c: { role: Role | null; entity_type: EntityType | null; vat_regime: VatRegime | null }): number {
  // Producteur : taux de son régime TVA. Sans régime : une personne physique est non assujettie (convention PP), une société à 21 %.
  if (c.role === 'producer') return c.vat_regime ? VAT_RATES[c.vat_regime] : (c.entity_type === 'physique' ? 0 : 0.21);
  return c.entity_type === 'physique' ? 0.06 : 0.21;
}

export const VAT_RATE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: '0 %' },
  { value: 0.06, label: '6 %' },
  { value: 0.21, label: '21 %' },
];

/* ── Lecture ── */

export async function fetchParticipantsByIds(ids: string[]): Promise<ParticipantRow[]> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const out: ParticipantRow[] = [];
  for (let i = 0; i < unique.length; i += 150) {
    const { data, error } = await supabase.from('participants').select(PARTICIPANT_COLUMNS).in('id', unique.slice(i, i + 150));
    if (error) throw error;
    out.push(...((data ?? []) as ParticipantRow[]));
  }
  return out;
}

/** Participants d'un groupe (même nom de groupe, sans tenir compte des majuscules). */
export async function fetchGroupParticipants(groupe: string): Promise<ParticipantRow[]> {
  const { data, error } = await supabase.from('participants').select(PARTICIPANT_COLUMNS).ilike('groupe', escapeLike(groupe));
  if (error) throw error;
  return ((data ?? []) as ParticipantRow[]).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'fr'));
}

export interface CompanyBlock {
  /** N° BCE au format « BE 0123.456.789 », ou null pour les EAN sans n° d'entreprise. */
  companyNumber: string | null;
  companyName: string | null;
  participants: ParticipantRow[];
}

/** Participants d'un groupe rassemblés par n° d'entreprise (les plus grands blocs d'abord, les EAN sans n° à la fin). */
export function groupByCompany(rows: ParticipantRow[]): CompanyBlock[] {
  const blocks = new Map<string, CompanyBlock>();
  for (const p of rows) {
    const bce = formatBce(p.company_number);
    const key = bce ?? '';
    const block = blocks.get(key) ?? { companyNumber: bce, companyName: null, participants: [] };
    block.participants.push(p);
    if (!block.companyName && p.company_name?.trim()) block.companyName = p.company_name.trim();
    blocks.set(key, block);
  }
  return Array.from(blocks.values()).sort((a, b) => {
    if (!a.companyNumber !== !b.companyNumber) return a.companyNumber ? -1 : 1;
    return b.participants.length - a.participants.length || (a.companyName ?? '').localeCompare(b.companyName ?? '', 'fr');
  });
}

export interface GroupStats {
  eans: number;
  /** Nombre de n° d'entreprise différents */
  companies: number;
  roles: Partial<Record<Role, number>>;
}

/** Chiffres de chaque groupe pour les cartes du pipeline (clé : nom du groupe en minuscules). */
export async function fetchGroupStats(): Promise<Record<string, GroupStats>> {
  const { data, error } = await supabase.from('participants').select('groupe, company_number, type').not('groupe', 'is', null);
  if (error) throw error;
  const stats: Record<string, GroupStats & { bce: Set<string> }> = {};
  for (const p of (data ?? []) as { groupe: string | null; company_number: string | null; type: Role | null }[]) {
    const key = lower(p.groupe);
    if (!key) continue;
    const s = (stats[key] ??= { eans: 0, companies: 0, roles: {}, bce: new Set<string>() });
    s.eans += 1;
    const bce = formatBce(p.company_number);
    if (bce) s.bce.add(bce);
    const role = p.type ?? 'consumer';
    s.roles[role] = (s.roles[role] ?? 0) + 1;
  }
  return Object.fromEntries(Object.entries(stats).map(([k, s]) => [k, { eans: s.eans, companies: s.bce.size, roles: s.roles }]));
}

const ROLE_PLURALS: Record<Role, [string, string]> = {
  consumer: ['consommateur', 'consommateurs'],
  producer: ['producteur', 'producteurs'],
  prosumer: ['prosumer', 'prosumers'],
};

/** « 9 consommateurs · 1 prosumer » */
export function rolesSummary(roles: Partial<Record<Role, number>>): string {
  return (['consumer', 'producer', 'prosumer'] as Role[])
    .filter(r => (roles[r] ?? 0) > 0)
    .map(r => `${roles[r]} ${ROLE_PLURALS[r][(roles[r] ?? 0) > 1 ? 1 : 0]}`)
    .join(' · ');
}

/* ── Fiche participant → CRM ── */

function vatRegimeFromRate(rate: number | null, current: VatRegime | null | undefined): VatRegime | null {
  if (rate === null || rate === undefined) return current ?? null;
  if (rate >= 0.2) return 'assujetti';
  if (rate === 0) return current === 'franchise' ? 'franchise' : 'non_assujetti';
  return current ?? null;
}

/**
 * Valeurs actuelles des fiches participants d'un membre actif (non groupé), à afficher dans sa fiche CRM.
 * La première fiche porte les valeurs communes (société, prix, date d'entrée…) ; chaque compteur reprend son nom et son adresse.
 */
export function contactFromParticipants(c: CrmContact, rows: ParticipantRow[]): CrmContactDraft {
  const byId = new Map(rows.map(r => [r.id, r]));
  const linked = c.meters.map(m => (m.participant_id ? byId.get(m.participant_id) : undefined));
  const first = linked.find(Boolean);
  if (!first) return {};

  const meters: Meter[] = c.meters.map((m, i) => {
    const p = linked[i];
    return p ? { ...m, name: p.name?.trim() || m.name, meter_address: p.address?.trim() || m.meter_address } : m;
  });
  const role: Role = first.type ?? c.role ?? 'consumer';
  const patch: CrmContactDraft = {
    meters,
    role,
    billing_email: first.send_email && lower(first.send_email) !== lower(c.email) ? first.send_email.trim().toLowerCase() : null,
    sharing_start_date: first.entry_date ?? c.sharing_start_date,
    sharing_operation_id: first.sharing_operation_id ?? c.sharing_operation_id,
    annual_fee: first.adhesion_fee ?? c.annual_fee,
    sharing_vat_rate: first.vat_rate ?? null,
    reimbursement_period: role === 'consumer' ? null : first.reimbursement_period ?? 'quarterly',
    vat_regime: role === 'consumer' ? c.vat_regime : vatRegimeFromRate(first.vat_rate, c.vat_regime),
  };
  if (c.entity_type === 'morale') {
    patch.company_number = formatBce(first.company_number) ?? c.company_number;
    patch.company_name = first.company_name?.trim() || c.company_name;
  }
  if (first.shared_energy_formula_type !== 'variable') {
    patch.sharing_price_cents_kwh = fromMwh(role === 'prosumer' ? first.prosumer_buy_price : first.shared_energy_price);
    patch.sharing_sell_price_cents_kwh = role === 'prosumer' ? fromMwh(first.prosumer_sell_price) : null;
  }
  return patch;
}

/** Le prix du membre est indexé (formule A × X) : il se modifie dans le tableau de bord de l'opération. */
export function hasVariablePrice(rows: ParticipantRow[]): boolean {
  return rows.some(r => r.shared_energy_formula_type === 'variable');
}

/* ── CRM → fiche participant ── */

/** Colonnes communes à toutes les fiches participants d'un membre, calculées depuis sa fiche CRM. */
function sharedColumns(c: CrmContactDraft, variablePrice: boolean): Record<string, unknown> {
  const role: Role = c.role ?? 'consumer';
  const morale = c.entity_type === 'morale';
  const cols: Record<string, unknown> = {
    send_email: (c.billing_email?.trim() || c.email?.trim() || '').toLowerCase() || null,
    type: role,
    company_number: morale ? formatBce(c.company_number) : null,
    company_name: morale ? c.company_name?.trim() || null : null,
    groupe: c.groupe?.trim() || null,
    entry_date: c.sharing_start_date ?? null,
    sharing_operation_id: c.sharing_operation_id ?? null,
    adhesion_fee: c.annual_fee ?? null,
    vat_rate: c.sharing_vat_rate ?? null,
    // Producteur ou prosumer : période de remboursement (trimestrielle par défaut) ; consommateur : toujours mensuel.
    reimbursement_period: role === 'consumer' ? null : c.reimbursement_period ?? 'quarterly',
  };
  if (!variablePrice) {
    const buy = toMwh(c.sharing_price_cents_kwh);
    // Comme dans le formulaire admin, un prosumer garde aussi son prix d'achat dans shared_energy_price.
    cols.shared_energy_price = buy;
    cols.prosumer_buy_price = role === 'prosumer' ? buy : null;
    cols.prosumer_sell_price = role === 'prosumer' ? toMwh(c.sharing_sell_price_cents_kwh) : null;
  }
  return cols;
}

/**
 * Modifications à écrire dans chaque fiche participant après l'enregistrement d'une fiche CRM :
 * seuls les champs modifiés sont écrits, les autres données de la fiche (tarifs fournisseur, formules…) restent intactes.
 */
export function participantPatches(before: CrmContactDraft, after: CrmContactDraft, variablePrice: boolean): Map<string, Record<string, unknown>> {
  const b = sharedColumns(before, variablePrice);
  const a = sharedColumns(after, variablePrice);
  const shared: Record<string, unknown> = {};
  for (const k of Object.keys(a)) if (JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null)) shared[k] = a[k];

  const out = new Map<string, Record<string, unknown>>();
  const beforeMeters = new Map((before.meters ?? []).filter(m => m.participant_id).map(m => [m.participant_id as string, m]));
  for (const m of after.meters ?? []) {
    if (!m.participant_id) continue;
    const prev = beforeMeters.get(m.participant_id);
    const patch: Record<string, unknown> = { ...shared };
    if ((m.name?.trim() || null) !== (prev?.name?.trim() || null)) patch.name = m.name?.trim() || null;
    if ((m.meter_address?.trim() || null) !== (prev?.meter_address?.trim() || null)) patch.address = m.meter_address?.trim() || null;
    if (Object.keys(patch).length > 0) out.set(m.participant_id, patch);
  }
  return out;
}

const REQUIRED_COLUMNS: Record<string, string> = {
  name: 'Le nom du participant est requis',
  address: "L'adresse est requise",
  entry_date: "La date d'entrée est requise",
  shared_energy_price: "Le prix de l'énergie partagée est requis",
  prosumer_sell_price: 'Le prix de vente est requis pour un prosumer',
  sharing_operation_id: "L'opération de partage est requise",
};

/** Mêmes champs obligatoires que le formulaire participant de la page admin. */
export function participantPatchErrors(patches: Map<string, Record<string, unknown>>, role: Role | null | undefined): string[] {
  const errors = new Set<string>();
  for (const patch of patches.values()) {
    for (const [col, message] of Object.entries(REQUIRED_COLUMNS)) {
      if (!(col in patch)) continue;
      if (col === 'prosumer_sell_price' && role !== 'prosumer') continue;
      const v = patch[col];
      if (v === null || v === undefined || String(v).trim() === '') errors.add(message);
    }
  }
  return Array.from(errors);
}

export async function updateParticipant(id: string, patch: Record<string, unknown>): Promise<ParticipantRow> {
  const { data, error } = await supabase.from('participants').update(patch).eq('id', id).select(PARTICIPANT_COLUMNS).maybeSingle();
  if (error) throw error;
  if (!data) throw await noRowError('participants', id, 'Fiche participant');
  return data as ParticipantRow;
}

/** Écrit les modifications dans les fiches participants. Renvoie le nombre de fiches mises à jour. */
export async function applyParticipantPatches(patches: Map<string, Record<string, unknown>>): Promise<number> {
  for (const [id, patch] of patches) await updateParticipant(id, patch);
  return patches.size;
}
